import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Contact, ConversationMessage, Channel } from "@/lib/types";
import { conversations, contacts, sequences } from "@/lib/data";
import { useState, useEffect } from "react";
import {
	Sparkles,
	Send,
	Mail,
	Linkedin,
	Smartphone,
	ArrowUpRight,
	Copy,
	RotateCw,
	User,
	Bot,
	Check,
} from "lucide-react";

const channelConfig: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
	email: { icon: <Mail className="size-4" />, label: "Email", color: "text-neon-cyan" },
	linkedin: { icon: <Linkedin className="size-4" />, label: "LinkedIn", color: "text-neon-blue" },
	sms: { icon: <Smartphone className="size-4" />, label: "SMS", color: "text-neon-green" },
};

const contactsWithConvos = contacts.filter((c) =>
	conversations.some((msg) => msg.contactId === c.id)
);

const sampleFollowUps: Record<string, string[]> = {
	"c2": [
		"Hi Marcus,\n\nGreat to hear you're exploring tools to improve your outbound motion! SequenceAI integrates natively with HubSpot — we sync contacts, activities, and deal stages bidirectionally in real-time.\n\nAs for pricing, we have flexible plans starting at $79/seat/month for teams. I'd love to walk you through the specifics based on your team size.\n\nWould tomorrow at 2pm work for a quick call?",
		"Hey Marcus,\n\nThanks for your interest! Quick answers:\n\n1. **HubSpot Integration**: Full native integration — contacts, sequences, and analytics all sync automatically\n2. **Pricing**: Starts at $79/seat/month, with volume discounts for larger teams\n\nI can set up a personalized demo showing the HubSpot integration in action. What does your calendar look like this week?",
		"Marcus,\n\nAppreciate the response! To give you the best pricing and integration overview, it would help to understand your team size and current HubSpot setup.\n\nI have a quick 3-question form that takes 30 seconds — then I can send over a custom proposal. Would that work?",
	],
	"c7": [
		"Hi Aisha,\n\nThursday at 2pm works perfectly! I'll send over a calendar invite.\n\nFor the demo, I'll make sure to cover:\n- AI-powered message writing with context awareness\n- LinkedIn automation (connection requests, messages, InMail)\n- Our analytics dashboard\n\nLooking forward to showing you what SequenceAI can do for Innovate Co!",
		"Aisha,\n\nThursday at 2pm is great — calendar invite coming your way!\n\nSince you mentioned AI writing and LinkedIn automation, I'll prepare a tailored demo focusing on those features. I'll also show you some results from companies similar to Innovate Co.\n\nSee you Thursday!",
	],
};

function getInitials(name: string) {
	return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

export function AIWriter() {
	const [selectedContactId, setSelectedContactId] = useState<string>(
		contactsWithConvos[0]?.id || ""
	);
	const [channel, setChannel] = useState<Channel>("email");
	const [customContext, setCustomContext] = useState("");
	const [generatedMessages, setGeneratedMessages] = useState<string[]>([]);
	const [isGenerating, setIsGenerating] = useState(false);
	const [selectedMessageIdx, setSelectedMessageIdx] = useState<number | null>(null);
	const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

	const selectedContact = contacts.find((c) => c.id === selectedContactId);
	const contactConversation = conversations
		.filter((msg) => msg.contactId === selectedContactId)
		.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

	function handleGenerate() {
		setIsGenerating(true);
		setGeneratedMessages([]);
		setSelectedMessageIdx(null);

		// Simulate AI generation
		setTimeout(() => {
			const msgs = sampleFollowUps[selectedContactId] || [
				`Hi ${selectedContact?.name?.split(" ")[0]},\n\nThank you for your response. I'd love to continue our conversation and explore how SequenceAI can help ${selectedContact?.company}.\n\nWould you have 15 minutes this week for a quick call?`,
			];
			setGeneratedMessages(msgs);
			setIsGenerating(false);
		}, 1500);
	}

	function handleCopy(idx: number) {
		navigator.clipboard.writeText(generatedMessages[idx]);
		setCopiedIdx(idx);
		setTimeout(() => setCopiedIdx(null), 2000);
	}

	return (
		<div className="p-6 h-[calc(100vh-0px)] flex flex-col">
			{/* Header */}
			<div className="mb-6">
				<div className="flex items-center gap-2 mb-1">
					<Sparkles className="size-5 text-neon-purple" />
					<h1 className="text-2xl font-bold">AI Follow-up Writer</h1>
				</div>
				<p className="text-muted-foreground text-sm">
					Generate context-aware follow-up messages based on conversation history
				</p>
			</div>

			<div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0">
				{/* Left: Config + Conversation */}
				<div className="lg:col-span-5 flex flex-col gap-4 min-h-0">
					{/* Config */}
					<Card className="border-border/50 shrink-0">
						<CardContent className="pt-4 pb-4 space-y-3">
							<div className="grid grid-cols-2 gap-3">
								<div>
									<label className="text-xs text-muted-foreground mb-1 block">Contact</label>
									<Select value={selectedContactId} onValueChange={setSelectedContactId}>
										<SelectTrigger className="bg-secondary border-border">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{contactsWithConvos.map((c) => (
												<SelectItem key={c.id} value={c.id}>
													{c.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div>
									<label className="text-xs text-muted-foreground mb-1 block">Channel</label>
									<Select value={channel} onValueChange={(v) => setChannel(v as Channel)}>
										<SelectTrigger className="bg-secondary border-border">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="email">Email</SelectItem>
											<SelectItem value="linkedin">LinkedIn</SelectItem>
											<SelectItem value="sms">SMS</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>
							<div>
								<label className="text-xs text-muted-foreground mb-1 block">
									Additional Context (optional)
								</label>
								<Input
									placeholder="e.g., Focus on pricing, mention the webinar..."
									value={customContext}
									onChange={(e) => setCustomContext(e.target.value)}
									className="bg-secondary border-border"
								/>
							</div>
							<Button
								onClick={handleGenerate}
								disabled={isGenerating}
								className="w-full bg-gradient-to-r from-neon-purple to-neon-cyan text-white font-semibold hover:opacity-90"
							>
								{isGenerating ? (
									<>
										<RotateCw className="size-4 mr-2 animate-spin" />
										Generating...
									</>
								) : (
									<>
										<Sparkles className="size-4 mr-2" />
										Generate Follow-ups
									</>
								)}
							</Button>
						</CardContent>
					</Card>

					{/* Conversation History */}
					<Card className="border-border/50 flex-1 min-h-0 flex flex-col">
						<div className="px-4 pt-4 pb-2 shrink-0">
							<h3 className="font-semibold text-sm">Conversation History</h3>
							{selectedContact && (
								<p className="text-xs text-muted-foreground mt-0.5">
									{selectedContact.name} at {selectedContact.company}
								</p>
							)}
						</div>
						<Separator />
						<ScrollArea className="flex-1">
							<div className="p-4 space-y-3">
								{contactConversation.map((msg) => {
									const chConf = channelConfig[msg.channel];
									return (
										<div
											key={msg.id}
											className={`flex gap-3 ${msg.direction === "outbound" ? "" : "flex-row-reverse"}`}
										>
											<div
												className={`size-7 rounded-full flex items-center justify-center shrink-0 ${
													msg.direction === "outbound"
														? "bg-neon-cyan/10 text-neon-cyan"
														: "bg-neon-purple/10 text-neon-purple"
												}`}
											>
												{msg.direction === "outbound" ? (
													<Bot className="size-3.5" />
												) : (
													<User className="size-3.5" />
												)}
											</div>
											<div
												className={`flex-1 max-w-[85%] rounded-lg p-3 text-sm ${
													msg.direction === "outbound"
														? "bg-secondary"
														: "bg-neon-purple/5 border border-neon-purple/20"
												}`}
											>
												<div className="flex items-center gap-1.5 mb-1">
													<span className={`${chConf.color}`}>{chConf.icon}</span>
													<span className="text-xs text-muted-foreground">
														{msg.direction === "outbound" ? "You" : selectedContact?.name?.split(" ")[0]}
													</span>
													<span className="text-xs text-muted-foreground ml-auto">
														{new Date(msg.timestamp).toLocaleDateString("en-US", {
															month: "short",
															day: "numeric",
														})}
													</span>
												</div>
												<p className="whitespace-pre-line text-sm leading-relaxed">{msg.content}</p>
											</div>
										</div>
									);
								})}
								{contactConversation.length === 0 && (
									<p className="text-sm text-muted-foreground text-center py-8">
										No conversation history
									</p>
								)}
							</div>
						</ScrollArea>
					</Card>
				</div>

				{/* Right: Generated Messages */}
				<div className="lg:col-span-7 flex flex-col min-h-0">
					<Card className="border-border/50 flex-1 min-h-0 flex flex-col">
						<div className="px-4 pt-4 pb-2 flex items-center justify-between shrink-0">
							<div>
								<h3 className="font-semibold text-sm">Generated Follow-ups</h3>
								<p className="text-xs text-muted-foreground mt-0.5">
									{generatedMessages.length > 0
										? `${generatedMessages.length} variations generated`
										: "Configure options and generate"}
								</p>
							</div>
							{generatedMessages.length > 0 && (
								<Button
									variant="ghost"
									size="sm"
									onClick={handleGenerate}
									className="text-neon-cyan hover:text-neon-cyan"
								>
									<RotateCw className="size-3.5 mr-1.5" />
									Regenerate
								</Button>
							)}
						</div>
						<Separator />
						<ScrollArea className="flex-1">
							<div className="p-4 space-y-4">
								{isGenerating && (
									<div className="flex flex-col items-center justify-center py-16 gap-3">
										<div className="size-12 rounded-full bg-gradient-to-r from-neon-purple to-neon-cyan flex items-center justify-center animate-pulse">
											<Sparkles className="size-5 text-white" />
										</div>
										<p className="text-sm text-muted-foreground">
											Analyzing conversation context...
										</p>
										<div className="flex gap-1">
											<div className="size-1.5 rounded-full bg-neon-cyan animate-bounce [animation-delay:0ms]" />
											<div className="size-1.5 rounded-full bg-neon-purple animate-bounce [animation-delay:150ms]" />
											<div className="size-1.5 rounded-full bg-neon-green animate-bounce [animation-delay:300ms]" />
										</div>
									</div>
								)}

								{!isGenerating && generatedMessages.length === 0 && (
									<div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
										<div className="size-16 rounded-2xl bg-secondary flex items-center justify-center">
											<Sparkles className="size-7" />
										</div>
										<p className="text-sm">Select a contact and generate follow-ups</p>
										<p className="text-xs">
											The AI will analyze the conversation history and craft personalized messages
										</p>
									</div>
								)}

								{generatedMessages.map((msg, idx) => (
									<Card
										key={idx}
										className={`border-border/50 cursor-pointer transition-all ${
											selectedMessageIdx === idx
												? "border-neon-cyan/50"
												: "hover:border-border"
										}`}
										onClick={() => setSelectedMessageIdx(idx)}
									>
										<CardContent className="py-3 px-4">
											<div className="flex items-center justify-between mb-2">
												<div className="flex items-center gap-2">
													<Badge variant="secondary" className="text-xs">
														Option {idx + 1}
													</Badge>
													{selectedMessageIdx === idx && (
														<Badge className="bg-neon-cyan/15 text-neon-cyan border-neon-cyan/30 text-xs">
															Selected
														</Badge>
													)}
												</div>
												<div className="flex items-center gap-1">
													<Button
														variant="ghost"
														size="icon"
														className="size-7"
														onClick={(e) => {
															e.stopPropagation();
															handleCopy(idx);
														}}
													>
														{copiedIdx === idx ? (
															<Check className="size-3.5 text-neon-green" />
														) : (
															<Copy className="size-3.5" />
														)}
													</Button>
												</div>
											</div>
											<p className="text-sm whitespace-pre-line leading-relaxed">
												{msg}
											</p>
										</CardContent>
									</Card>
								))}
							</div>
						</ScrollArea>

						{/* Send Bar */}
						{selectedMessageIdx !== null && (
							<>
								<Separator />
								<div className="p-3 flex items-center gap-2 shrink-0">
									<div className="flex items-center gap-2 text-xs text-muted-foreground flex-1">
										<span>Sending to</span>
										<Badge variant="secondary">
											{selectedContact?.name}
										</Badge>
										<span>via</span>
										<Badge variant="secondary" className={channelConfig[channel].color}>
											{channelConfig[channel].icon}
											<span className="ml-1">{channelConfig[channel].label}</span>
										</Badge>
									</div>
									<Button className="bg-neon-cyan text-black font-semibold hover:bg-neon-cyan/90">
										<Send className="size-3.5 mr-2" />
										Send Message
									</Button>
								</div>
							</>
						)}
					</Card>
				</div>
			</div>
		</div>
	);
}
